import objectPath from 'object-path'
import {
  hasOwn,
  REQUIRE_FIELD_CONFIG_CODE,
  REQUIRE_OPTION_CONFIG_CODE,
  resolveRequireConfigIdentity,
  resolveRequiredValue,
} from 'components/RequireConfig/config'

export const PARAM_LIST = ['system', 'variables', 'list']

export const ROUTE_HIS = '/his#'

export const ROUTE_API = '/api'

export const isExternalApiUrl = (linkType, url = '') =>
  linkType === 'external' && url.startsWith(ROUTE_API)

export const PRINT_PATH = '/print/report'

export const PRESET_COLORS = [
  'transparent',
  '#000000',
  '#343a40',
  '#4A4A4A',
  '#6c757d',
  '#9B9B9B',
  '#f8f9fa',
  '#FFFFFF',
  '#D0021B',
  '#FF5370',
  '#e83e8c',
  '#BD10E0',
  '#9013FE',
  '#6f42c1',
  '#6610f2',
  '#4A90E2',
  '#4099ff',
  '#00bcd4',
  '#50E3C2',
  '#2ed8b6',
  '#20c997',
  '#7ED321',
  '#417505',
  '#B8E986',
  '#F8E71C',
  '#F5A623',
  '#FFB64D',
  '#fd7e14',
  '#8B572A',
  '#ffc9c9',
  '#fcc2d7',
  '#eebefa',
  '#d0bfff',
  '#bac8ff',
  '#74c0fc',
  '#99e9f2',
  '#96f2d7',
  '#b2f2bb',
  '#d8f5a2',
  '#fff3bf',
  '#ffe8cc',
]

export const ACTIVE_COLOR = '#4099ff'

export const CONFIG_NAME = {
  visibleUi: 'designer_visible_ui_option',
  defaultValue: 'designer_default_value',
  requireField: REQUIRE_FIELD_CONFIG_CODE,
  requireOption: REQUIRE_OPTION_CONFIG_CODE,
}

export const SCORE_VISIBLE_CONFIG_KEY = '@scoreChart'

const getConfigFormCodes = (jsonForm = {}) => {
  const codes = Array.isArray(jsonForm.configFormCode)
    ? jsonForm.configFormCode
    : [jsonForm.configFormCode]

  return [...new Set([...codes, jsonForm.formCode].filter(Boolean))]
}

const getConfigValueByFormCode = (targetConfig = {}, jsonForm = {}, key) => {
  const keys = Array.isArray(key) ? key.filter(Boolean) : [key].filter(Boolean)
  let value
  let hasValue = false

  getConfigFormCodes(jsonForm).some((formCode) => {
    const config = objectPath.get(targetConfig, ['jsonData', formCode]) || {}
    keys.some((targetKey) => {
      hasValue = objectPath.has(config, targetKey)
      if (hasValue) {
        value = objectPath.get(config, targetKey)
      }
      return hasValue
    })
    return hasValue
  })

  return { hasValue, value }
}

const getUiConfigKeys = (ui = {}) =>
  [ui.key, ui.name, ui.fieldName].filter(Boolean)

const getRequireConfigIdentity = (jsonForm = {}) => {
  if (jsonForm.requireConfigIdentity?.scopeKey) {
    return jsonForm.requireConfigIdentity
  }

  const formCodes = getConfigFormCodes(jsonForm)
  return resolveRequireConfigIdentity({
    formCode: jsonForm.formCode || formCodes[0],
  })
}

const getSystemRequired = (jsonForm = {}, keys = [], fallback = false) => {
  const require = jsonForm.require || {}
  const input = require.input || {}
  const checked = require.checked || {}
  const base = jsonForm.requireConfigBase || {}

  for (const key of keys) {
    if (hasOwn(input, key)) return Boolean(input[key])
    if (hasOwn(checked, key) && hasOwn(checked[key], 'require')) {
      return Boolean(checked[key].require)
    }
    if (hasOwn(base, key)) return Boolean(base[key])
  }

  return Boolean(fallback)
}

export const getShowUiConfig = ({ uischema, config, jsonForm }) => {
  const visibleUiConfig =
    config.find(({ configCode }) => configCode === CONFIG_NAME.visibleUi) || {}
  let oldConfig = getConfigValueByFormCode(
    visibleUiConfig,
    jsonForm,
    uischema.key,
  )
  if (oldConfig.hasValue) {
    return oldConfig.value
  }
  return uischema.isShowUi
}

export const getScoreVisibleConfigPath = (uiKey, score = {}) => [
  SCORE_VISIBLE_CONFIG_KEY,
  uiKey,
  score.key || score.name,
]

const getScoreVisibleConfigValue = ({
  visibleUiConfig,
  jsonForm,
  uiKey,
  score,
}) => {
  let scoreKeys = [score.key, score.name].filter(Boolean)
  let value
  let hasValue = false

  getConfigFormCodes(jsonForm).some((formCode) => {
    const config = objectPath.get(visibleUiConfig, ['jsonData', formCode]) || {}
    scoreKeys.some((scoreKey) => {
      let path = [SCORE_VISIBLE_CONFIG_KEY, uiKey, scoreKey]
      hasValue = objectPath.has(config, path)
      if (hasValue) {
        value = objectPath.get(config, path)
      }
      return hasValue
    })
    return hasValue
  })

  return hasValue ? value : score.enabled
}

export const getScoreVisibleConfig = ({
  uischema = {},
  config = [],
  jsonForm = {},
}) => {
  const visibleUiConfig =
    config.find(({ configCode }) => configCode === CONFIG_NAME.visibleUi) || {}

  const applyScoreConfig = (score = {}) => ({
    ...score,
    enabled: getScoreVisibleConfigValue({
      visibleUiConfig,
      jsonForm,
      uiKey: uischema.key,
      score,
    }),
    ...(Array.isArray(score.data)
      ? { data: score.data.map((item) => applyScoreConfig(item)) }
      : {}),
  })

  return (uischema.scoreConfig || []).map((score) => applyScoreConfig(score))
}

export const getRequireFieldConfig = ({
  key,
  config = [],
  jsonForm,
  systemRequired,
}) => {
  const keys = Array.isArray(key) ? key.filter(Boolean) : [key].filter(Boolean)
  const identity = getRequireConfigIdentity(jsonForm)
  const requireOptionConfig =
    config.find(({ configCode }) => configCode === CONFIG_NAME.requireOption) ||
    {}
  const requireFieldConfig =
    config.find(({ configCode }) => configCode === CONFIG_NAME.requireField) ||
    {}

  return resolveRequiredValue({
    fieldKey: keys[0],
    fieldAliases: keys.slice(1),
    scopeKey: identity.scopeKey,
    aliases: identity.aliases,
    optionConfig: requireOptionConfig,
    requireFieldConfig,
    pending: jsonForm.requireConfigPending || {},
    systemRequired:
      systemRequired === undefined
        ? getSystemRequired(jsonForm, keys)
        : systemRequired,
  }).value
}

export const getRequireFieldKeys = ({ config = [], jsonForm }) => {
  const identity = getRequireConfigIdentity(jsonForm)
  const requireOptionConfig =
    config.find(({ configCode }) => configCode === CONFIG_NAME.requireOption) ||
    {}
  const requireFieldConfig =
    config.find(({ configCode }) => configCode === CONFIG_NAME.requireField) ||
    {}
  const optionScope =
    requireOptionConfig?.jsonData?.[identity.scopeKey] || {}
  const requireFieldKeys = [identity.scopeKey, ...identity.aliases].reduce(
    (keys, configScopeKey) => [
      ...keys,
      ...Object.keys(
        requireFieldConfig?.jsonData?.[configScopeKey] || {},
      ),
    ],
    [],
  )
  const require = jsonForm.require || {}
  const input = require.input || {}
  const checked = require.checked || {}
  const candidates = [
    ...Object.keys(jsonForm.requireConfigPending || {}),
    ...Object.keys(optionScope),
    ...requireFieldKeys,
    ...Object.keys(input),
    ...Object.keys(checked),
    ...Object.keys(jsonForm.requireConfigBase || {}),
  ]

  return [...new Set(candidates)].filter((fieldKey) =>
    getRequireFieldConfig({ key: fieldKey, config, jsonForm }),
  )
}

export const applyRequireFieldConfig = (jsonForm, config = []) => {
  const identity = getRequireConfigIdentity(jsonForm)
  objectPath.set(jsonForm, 'requireConfigIdentity', identity)
  const requireConfigBase = jsonForm.requireConfigBase || {}

  const applyConfig = (ui = {}) => {
    if (ui.key) {
      if (!hasOwn(requireConfigBase, ui.key)) {
        requireConfigBase[ui.key] = Boolean(ui.require)
      }
      objectPath.set(
        ui,
        'require',
        getRequireFieldConfig({
          key: getUiConfigKeys(ui),
          config,
          jsonForm,
          systemRequired: getSystemRequired(
            jsonForm,
            getUiConfigKeys(ui),
            requireConfigBase[ui.key],
          ),
        }),
      )
    }
    if (ui?.input?.key) {
      if (!hasOwn(requireConfigBase, ui.input.key)) {
        requireConfigBase[ui.input.key] = Boolean(ui.input.require)
      }
      objectPath.set(
        ui,
        'input.require',
        getRequireFieldConfig({
          key: getUiConfigKeys(ui.input),
          config,
          jsonForm,
          systemRequired: getSystemRequired(
            jsonForm,
            getUiConfigKeys(ui.input),
            requireConfigBase[ui.input.key],
          ),
        }),
      )
    }
    let elements = ui.elements || []
    elements.forEach((element) => applyConfig(element))
  }

  let elements = objectPath.get(jsonForm, 'uischema.elements') || []
  objectPath.set(jsonForm, 'requireConfigBase', requireConfigBase)
  elements.forEach((ui) => applyConfig(ui))
  return jsonForm
}
